import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { bytes, fmtDate, relTime } from '../lib/format.js';
import { IconHardDrive, IconDownload, IconUpload, IconServer, IconClose } from './Icons.js';

interface SnapshotRecord {
  Server_ID: number;
  Server_Name: string;
  source_machine: string;
  source_directory: string;
  key: string;
  size_bytes: number;
  status: 'in_progress' | 'complete' | 'failed';
  error?: string;
  started_at: string | number;
  completed_at?: string | number | null;
}

interface MachineRow { machine_name: string; online: boolean; server_count: number }

interface Props {
  Server_ID: number;
  Server_Name: string;
  /** The server must be OFFLINE to snapshot (cold capture). */
  serverOffline: boolean;
  /** Loading a snapshot mutates records + hardware — admin only. */
  canManage: boolean;
}

export default function SnapshotsPanel({ Server_ID, Server_Name, serverOffline, canManage }: Props) {
  const [snaps, setSnaps] = useState<SnapshotRecord[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loadTarget, setLoadTarget] = useState<SnapshotRecord | null>(null);

  const reload = () => {
    setListError(null);
    setSnaps(null);
    api.get<{ snapshots: SnapshotRecord[] }>(`/api/snapshots?Server_ID=${Server_ID}`)
      .then((r) => setSnaps(r.snapshots))
      .catch((err) => { setSnaps([]); setListError(err.message); });
  };
  useEffect(reload, [Server_ID]);

  const takeSnapshot = async () => {
    if (!serverOffline) return;
    if (!confirm(
      `Take a migration snapshot of "${Server_Name}"?\n\n` +
      `This captures the whole server folder (saves, oxide, config, bat) MINUS the game binaries, ` +
      `and uploads it to R2. The server must stay OFFLINE for the duration. Large servers can take a while.`,
    )) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post('/api/snapshots/trigger', { Server_ID });
      reload();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const download = async (key: string) => {
    try {
      const { url } = await api.get<{ url: string }>(`/api/snapshots/${encodeURIComponent(key)}/download-url`);
      window.open(url, '_blank');
    } catch (err) { setActionError((err as Error).message); }
  };

  return (
    <div className="card p-4">
      <header className="flex items-center justify-between mb-1">
        <div className="min-w-0">
          <h3 className="font-semibold text-fg">Snapshots</h3>
          <p className="text-[11px] text-fg-dim mt-0.5">Full-folder copy to move a server to new hardware</p>
        </div>
        <button
          onClick={takeSnapshot}
          disabled={busy || !serverOffline}
          className="btn-primary btn-sm gap-1.5 shrink-0"
          title={serverOffline ? 'Take a cold migration snapshot' : 'Server must be OFFLINE to snapshot'}
        >
          <IconUpload className="w-3.5 h-3.5" />
          {busy ? 'Snapshotting…' : 'Take snapshot'}
        </button>
      </header>

      {!serverOffline && (
        <p className="text-[11px] text-status-warn/90 flex items-center gap-1.5 mt-2.5">
          <span className="dot bg-status-warn shrink-0" />
          Server is online — stop it first (snapshots are taken cold).
        </p>
      )}

      {actionError && (
        <div className="text-xs text-status-error mt-3 px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30 flex items-start justify-between gap-2">
          <span className="min-w-0 break-words">{actionError}</span>
          <button onClick={() => setActionError(null)} className="shrink-0 text-status-error/70 hover:text-status-error" aria-label="Dismiss">
            <IconClose className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="mt-3">
      {snaps === null && !listError ? (
        <p className="text-sm text-fg-muted py-2">Loading…</p>
      ) : listError ? (
        <div className="py-6 flex flex-col items-center text-center gap-2">
          <div className="w-10 h-10 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
            <IconHardDrive />
          </div>
          <span className="text-sm text-fg-muted">Couldn’t load snapshots</span>
          <span className="text-[11px] text-fg-dim max-w-[80%] break-words">{listError}</span>
          <button onClick={reload} className="btn-ghost btn-sm mt-1">Retry</button>
        </div>
      ) : snaps!.length === 0 ? (
        <div className="py-6 flex flex-col items-center text-center gap-2">
          <div className="w-10 h-10 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
            <IconHardDrive />
          </div>
          <span className="text-sm text-fg-muted">No snapshots yet</span>
          <span className="text-[11px] text-fg-dim max-w-[80%]">Stop the server and take one to enable moving it to another machine.</span>
        </div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto -mx-1 px-1">
          {snaps!.map((s) => (
            <div key={s.key} className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-bg-hover/60 text-sm group transition-colors">
              <span className={`dot shrink-0 ${s.status === 'complete' ? 'bg-status-online' : s.status === 'failed' ? 'bg-status-error' : 'bg-status-warn'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-fg truncate" title={s.key}>{fmtDate(s.started_at)}</div>
                <div className="text-[10px] text-fg-dim truncate">
                  {bytes(s.size_bytes)} · from {s.source_machine} · {relTime(s.started_at)}
                  {s.error && <span className="text-status-error"> · {s.error}</span>}
                </div>
              </div>
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => download(s.key)} className="btn-icon !w-7 !h-7" title="Download" aria-label="Download">
                  <IconDownload className="w-3.5 h-3.5" />
                </button>
                {canManage && s.status === 'complete' && (
                  <button
                    onClick={() => setLoadTarget(s)}
                    className="btn-icon !w-7 !h-7 hover:!text-accent"
                    title="Load onto a machine"
                    aria-label="Load onto a machine"
                  >
                    <IconServer className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {loadTarget && (
        <LoadDialog
          snapshot={loadTarget}
          onClose={() => setLoadTarget(null)}
          onDone={() => { setLoadTarget(null); reload(); }}
        />
      )}
    </div>
  );
}

function LoadDialog({ snapshot, onClose, onDone }: {
  snapshot: SnapshotRecord;
  onClose: () => void;
  onDone: () => void;
}) {
  const [machines, setMachines] = useState<MachineRow[] | null>(null);
  const [target, setTarget] = useState('');
  const [mode, setMode] = useState<'migrate' | 'clone'>('migrate');
  const [dir, setDir] = useState(snapshot.source_directory || '');
  const [ip, setIp] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ machines: MachineRow[] }>('/api/machines')
      .then((r) => {
        setMachines(r.machines);
        // Default to the first online machine that isn't the source.
        const pick = r.machines.find((m) => m.online && m.machine_name !== snapshot.source_machine);
        if (pick) setTarget(pick.machine_name);
      })
      .catch((err) => setError(err.message));
  }, [snapshot.source_machine]);

  const submit = async () => {
    if (!target) { setError('choose a target machine'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ Server_ID: number; mode: string }>('/api/snapshots/load', {
        key: snapshot.key,
        target_machine: target,
        mode,
        target_directory: dir.trim() || undefined,
        Server_IP: ip.trim() || undefined,
        Server_Name: name.trim() || undefined,
      });
      alert(
        `Snapshot loaded (${res.mode}) as server #${res.Server_ID} on ${target}.\n\n` +
        `The server is STAGED and STOPPED — verify ports/IP/config, then start it from the panel.`,
      );
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-fg">Load snapshot onto a machine</h3>
          <button onClick={onClose} className="btn-icon !w-7 !h-7" aria-label="Close"><IconClose className="w-4 h-4" /></button>
        </header>

        <p className="text-[11px] text-fg-dim mb-4 break-all">
          {snapshot.Server_Name} · {bytes(snapshot.size_bytes)} · from {snapshot.source_machine}
        </p>

        <label className="block text-xs text-fg-muted mb-1">Target machine</label>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="input w-full mb-3"
          disabled={busy || machines === null}
        >
          {machines === null && <option>Loading…</option>}
          {machines?.map((m) => (
            <option key={m.machine_name} value={m.machine_name} disabled={!m.online}>
              {m.machine_name}{m.online ? '' : ' (offline)'} · {m.server_count} server(s)
            </option>
          ))}
        </select>

        <label className="block text-xs text-fg-muted mb-1">Mode</label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            onClick={() => setMode('migrate')}
            className={`px-3 py-2 rounded-md text-xs border text-left ${mode === 'migrate' ? 'border-accent bg-accent/10 text-fg' : 'border-bg-border text-fg-muted'}`}
          >
            <div className="font-medium">Migrate</div>
            <div className="text-[10px] text-fg-dim">Move this server (same #{snapshot.Server_ID}) to the new machine. Retire the old one.</div>
          </button>
          <button
            onClick={() => setMode('clone')}
            className={`px-3 py-2 rounded-md text-xs border text-left ${mode === 'clone' ? 'border-accent bg-accent/10 text-fg' : 'border-bg-border text-fg-muted'}`}
          >
            <div className="font-medium">Clone</div>
            <div className="text-[10px] text-fg-dim">Create a NEW server from this snapshot. Original keeps running.</div>
          </button>
        </div>

        <label className="block text-xs text-fg-muted mb-1">Target directory</label>
        <input value={dir} onChange={(e) => setDir(e.target.value)} placeholder={snapshot.source_directory || 'C:\\rustservers\\...'} className="input w-full mb-3 font-mono text-xs" disabled={busy} />

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <label className="block text-xs text-fg-muted mb-1">Server IP <span className="text-fg-dim">(optional)</span></label>
            <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="new public IP" className="input w-full text-xs" disabled={busy} />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Name override <span className="text-fg-dim">(optional)</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={mode === 'clone' ? `${snapshot.Server_Name} (clone)` : snapshot.Server_Name} className="input w-full text-xs" disabled={busy} />
          </div>
        </div>

        {error && (
          <div className="text-xs text-status-error mb-3 px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">{error}</div>
        )}

        <div className="text-[11px] text-fg-dim mb-4">
          The target agent will download the snapshot, extract it, and run a SteamCMD validate to rebuild game binaries.
          The server is left <strong className="text-fg-muted">staged and stopped</strong> — you start it manually after verifying.
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost btn-sm" disabled={busy}>Cancel</button>
          <button onClick={submit} className="btn-primary btn-sm gap-1.5" disabled={busy || !target}>
            <IconServer className="w-3.5 h-3.5" />
            {busy ? 'Loading…' : `Load (${mode})`}
          </button>
        </div>
      </div>
    </div>
  );
}
